import React, { useEffect, useState } from "react";

import {
    Button,
    FieldGrid,
    LabeledField,
    Modal,
    SectionCard,
    Stack,
    TextInput,
    UploadDropzone,
} from "shared/ui";
import { importDeliveryZones } from "services/menu/rest";
import { DeliveryZoneDto } from "services/menu/restModels";

import { useMenuSettingsContext } from "features/MenuSettings/context";

import { Hint, ZoneList } from "../styles";
import { parseDecimalInput, sanitizeDecimalInput } from "shared/lib/decimalInput";

interface DeliveryZonesDialogProps {
    open: boolean;
    onClose: () => void;
}

export const DeliveryZonesDialog = ({ open, onClose }: DeliveryZonesDialogProps) => {
    const { delivery, currencyId, currencyFormatter, notify } = useMenuSettingsContext();

    const [zones, setZones] = useState<DeliveryZoneDto[]>([]);
    const [uploading, setUploading] = useState<boolean>(false);

    const conditions = delivery.toAddress.deliveryConditions;

    useEffect(() => {
        if (!open) return;

        setZones(conditions.type === "ZonePrice" ? conditions.deliveryZones : []);
    }, [open, conditions]);

    const handleUpload = async (file: File) => {
        setUploading(true);
        try {
            const result = await importDeliveryZones(file);
            setZones(result.deliveryZones);
            notify.showSuccess("Зоны доставки загружены");
        } catch (e) {
            notify.showError("Не получилось загрузить зоны доставки", e);
        } finally {
            setUploading(false);
        }
    };

    const updateZone = (index: number, patch: Partial<DeliveryZoneDto>) =>
        setZones((prev) =>
            prev.map((zone, i) => (i === index ? { ...zone, ...patch } : zone))
        );

    const setZonePrice = (
        index: number,
        field: "fixedDeliveryPrice" | "freeDeliveryMinimalOrderPrice",
        value: string
    ) => {
        const clean = sanitizeDecimalInput(value);
        updateZone(index, {
            [field]:
                clean === ""
                    ? null
                    : {
                          currencyId,
                          price: currencyFormatter.toSend(parseDecimalInput(clean) ?? 0),
                      },
        });
    };

    const priceToShow = (price: { price: number } | null) =>
        price ? currencyFormatter.toShow(price.price, true) : "";

    const handleSubmit = () => {
        delivery.setDeliveryConditions({ type: "ZonePrice", deliveryZones: zones });
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            maxWidth="md"
            title="Зоны доставки"
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        Сохранить
                    </Button>
                </>
            }
        >
            <Hint>
                Нарисуйте зоны в{" "}
                <a
                    href="https://yandex.ru/map-constructor/"
                    target="_blank"
                    rel="noreferrer"
                >
                    конструкторе карт Яндекса
                </a>
                , экспортируйте их в GeoJSON и загрузите файл сюда.
            </Hint>

            <UploadDropzone
                onDrop={(files) => handleUpload(files[0])}
                hint={
                    uploading
                        ? "Загружаем…"
                        : zones.length > 0
                        ? `Загружено зон: ${zones.length}. Новый файл заменит их`
                        : "Файл GeoJSON"
                }
            />

            <ZoneList>
                {zones.map((zone, index) => (
                    <SectionCard key={`${zone.name}-${index}`} title={zone.name}>
                        <Stack>
                            <LabeledField label="Название зоны">
                                <TextInput
                                    value={zone.name}
                                    onChange={(value) => updateZone(index, { name: value })}
                                />
                            </LabeledField>
                            <FieldGrid>
                                <LabeledField label="Стоимость доставки">
                                    <TextInput
                                        value={priceToShow(zone.fixedDeliveryPrice)}
                                        onChange={(value) =>
                                            setZonePrice(index, "fixedDeliveryPrice", value)
                                        }
                                        placeholder="не задана"
                                    />
                                </LabeledField>
                                <LabeledField label="Бесплатно от суммы">
                                    <TextInput
                                        value={priceToShow(zone.freeDeliveryMinimalOrderPrice)}
                                        onChange={(value) =>
                                            setZonePrice(
                                                index,
                                                "freeDeliveryMinimalOrderPrice",
                                                value
                                            )
                                        }
                                        placeholder="не задана"
                                    />
                                </LabeledField>
                            </FieldGrid>
                        </Stack>
                    </SectionCard>
                ))}
            </ZoneList>
        </Modal>
    );
};
